/**
 * Quick Action Panel Component
 * 
 * Displays 2 contextual quick action cards + "More Options" based on the current page.
 * Uses react-router's useLocation to detect the current page context.
 * Uses vertical stacked layout for better readability.
 */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { QuickActionCard, type QuickAction } from "./QuickActionCard";
import { getQuickActionsForPath, getPageContext, SHOW_MORE_ACTION_ID } from "./pageContextConfig";
import { AllActionsModal } from "./AllActionsModal";
import { cn } from "@/lib/utils";

interface QuickActionPanelProps {
  onActionClick: (action: QuickAction) => void;
  isLoading?: boolean;
  className?: string;
}

export const QuickActionPanel = ({
  onActionClick,
  isLoading = false,
  className,
}: QuickActionPanelProps) => {
  const location = useLocation();
  const pageContext = getPageContext(location.pathname);
  const actions = getQuickActionsForPath(location.pathname);
  const [isAllActionsOpen, setIsAllActionsOpen] = useState(false);

  // Handle action click - check if it's "Show More"
  const handleActionClick = (action: QuickAction) => {
    if (action.id === SHOW_MORE_ACTION_ID) {
      setIsAllActionsOpen(true);
    } else {
      onActionClick(action);
    }
  };

  // Handle action selected from modal
  const handleModalActionSelect = (action: QuickAction) => {
    onActionClick(action);
  };

  return (
    <>
      <div className={cn("px-4 py-3 border-b border-gray6 bg-gray-50/50 backdrop-blur-sm", className)}>
        {/* Section Header */}
        <div className="flex items-center gap-1.5 mb-2.5">
          <Sparkles className="h-3 w-3 text-emerald-500" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
            {pageContext.pageName} Quick Actions
          </span>
        </div>

        {/* Action Cards - Grid Layout */}
        <div className="grid grid-cols-1 gap-2">
          {actions.map((action) => (
            <QuickActionCard
              key={action.id}
              action={action}
              onClick={handleActionClick}
              isLoading={isLoading}
            />
          ))}
        </div>
      </div>

      {/* All Actions Modal */}
      <AllActionsModal
        isOpen={isAllActionsOpen}
        onClose={() => setIsAllActionsOpen(false)}
        onActionSelect={handleModalActionSelect}
        pageName={pageContext.pageName}
        relevantCategories={pageContext.relevantCategories}
      />
    </>
  );
};

